import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { fetchTestimonials } from "../services/testimonialService";
import { 
  Quote, 
  Star, 
  Search, 
  CheckCircle2, 
  MessageSquareQuote, 
  ArrowRight, 
  Loader2,
  Calendar,
  Sparkles,
  PhoneCall
} from "lucide-react";

const TestimonialCard = ({ testimonial, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const reviewText = testimonial.review || "";
  const isLong = reviewText.length > 260;
  const displayText = isExpanded || !isLong ? reviewText : reviewText.slice(0, 260) + "...";

  const formattedDate = testimonial.created_at
    ? new Date(testimonial.created_at).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.08 }}
      className="bg-white rounded-3xl p-8 sm:p-9 shadow-md hover:shadow-xl transition-all duration-300 border border-[#E8E3DC] flex flex-col justify-between group hover:-translate-y-1 relative"
    >
      <div>
        {/* Top Meta: Stars & Quote Icon */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-1 text-amber-400">
            {Array.from({ length: testimonial.rating || 5 }).map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-amber-400" />
            ))}
          </div>
          <div className="w-10 h-10 rounded-full bg-tropical-50 flex items-center justify-center text-tropical-600 group-hover:scale-110 transition-transform">
            <Quote className="w-5 h-5 fill-current opacity-60" />
          </div>
        </div>

        {/* Review Text */}
        <p className="text-ink-700 font-normal leading-relaxed text-[15px] sm:text-[16px] whitespace-pre-line mb-6">
          "{displayText}"
        </p>

        {isLong && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-tropical-600 hover:text-tropical-700 font-bold text-[13px] tracking-wide uppercase transition-colors inline-block mb-4"
          >
            {isExpanded ? "Show Less" : "Read Full Story"}
          </button>
        )}
      </div>

      {/* Author & Footer Info */}
      <div className="pt-6 border-t border-gray-100 mt-auto flex items-center justify-between">
        <div>
          <h3 className="text-ink-900 font-bold font-display text-[17px] group-hover:text-tropical-600 transition-colors">
            {testimonial.name}
          </h3>
          <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium mt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Verified Client</span>
          </div>
        </div>

        {formattedDate && (
          <div className="flex items-center gap-1 text-xs text-ink-400">
            <Calendar className="w-3 h-3" />
            <span>{formattedDate}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials()
      .then((data) => {
        setTestimonials(data);
        setFilteredTestimonials(data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredTestimonials(testimonials);
    } else {
      const q = search.toLowerCase();
      setFilteredTestimonials(
        testimonials.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            (t.review && t.review.toLowerCase().includes(q))
        )
      );
    }
  }, [search, testimonials]);

  return (
    <main className="min-h-screen bg-brand-light flex flex-col font-sans">
      <Navbar isTransparent={false} />

      {/* Hero Header */}
      <div className="relative bg-ink-900 py-24 sm:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10" 
          style={{ backgroundImage: "radial-gradient(circle at 0% 100%, #14b8a6 0%, transparent 60%)" }} 
        />
        <div className="relative max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-tropical-400 text-[11px] font-bold uppercase tracking-[0.2em] mb-4">
                <Sparkles className="w-3.5 h-3.5 text-tropical-400" /> Client Experiences
              </div>
              <h1 className="text-[2.5rem] md:text-[3.8rem] font-bold text-white mb-6 font-display leading-tight">
                What Our Clients <br className="hidden sm:inline" /> Say About Us
              </h1>
              <p className="text-white/70 text-[15px] sm:text-[16px] max-w-xl leading-relaxed font-light">
                Discover why homeowners, buyers, and investors trust Tammy Donnelly and the 340 Real Estate team to guide their journey on St. John, USVI.
              </p>
            </div>

            {/* Search Input */}
            <div className="relative w-full lg:w-96 shrink-0">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-tropical-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search reviews by name or keyword..."
                className="w-full pl-11 pr-5 py-4 bg-white/10 border border-white/20 text-white rounded-xl placeholder:text-white/40 focus:outline-none focus:bg-white/20 focus:border-white/30 transition-all text-[13px]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Trust Stats Bar */}
      <div className="bg-white border-b border-[#E8E3DC] py-8 px-6 shadow-sm">
        <div className="max-w-[1200px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <p className="text-2xl sm:text-3xl font-bold text-ink-900 font-display">100%</p>
            <p className="text-xs text-ink-500 uppercase tracking-wider font-semibold mt-1">5-Star Satisfaction</p>
          </div>
          <div>
            <p className="text-2xl sm:text-3xl font-bold text-ink-900 font-display">15+ Years</p>
            <p className="text-xs text-ink-500 uppercase tracking-wider font-semibold mt-1">St. John Experience</p>
          </div>
          <div>
            <p className="text-2xl sm:text-3xl font-bold text-ink-900 font-display">{testimonials.length || "17"}+</p>
            <p className="text-xs text-ink-500 uppercase tracking-wider font-semibold mt-1">Verified Stories</p>
          </div>
          <div>
            <p className="text-2xl sm:text-3xl font-bold text-ink-900 font-display">Full-Service</p>
            <p className="text-xs text-ink-500 uppercase tracking-wider font-semibold mt-1">Sales & Villa Mgmt</p>
          </div>
        </div>
      </div>

      {/* Testimonials List */}
      <div className="max-w-[1200px] mx-auto w-full px-6 py-16 sm:py-20 flex-grow">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-28 gap-4 opacity-60">
            <Loader2 className="w-10 h-10 text-tropical-500 animate-spin" />
            <p className="text-[13px] font-bold uppercase tracking-widest text-ink-600">Loading Testimonials…</p>
          </div>
        ) : filteredTestimonials.length === 0 ? (
          <div className="text-center py-28 text-ink-600">
            <MessageSquareQuote className="w-12 h-12 mx-auto text-tropical-500 opacity-40 mb-4" />
            <p className="text-[1.3rem] font-bold text-ink-900 font-display">No testimonials matched your search</p>
            <p className="text-[14px] mt-1 text-ink-500">Try searching for a different keyword or name.</p>
            <button
              onClick={() => setSearch("")}
              className="mt-6 px-6 py-2.5 bg-ink-900 text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-ink-800 transition-colors"
            >
              Reset Search
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
            {filteredTestimonials.map((t, idx) => (
              <TestimonialCard key={t.id || idx} testimonial={t} index={idx} />
            ))}
          </div>
        )}

        {/* Bottom CTA Card */}
        <div className="mt-20 sm:mt-28 bg-ink-900 rounded-3xl p-8 sm:p-14 text-white relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
          <div 
            className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full opacity-10 bg-tropical-500 blur-3xl pointer-events-none" 
          />
          <div className="max-w-xl z-10">
            <p className="text-tropical-400 text-xs font-bold uppercase tracking-widest mb-2 font-display">
              Begin Your Island Journey
            </p>
            <h2 className="text-2xl sm:text-4xl font-bold font-display mb-4">
              Ready to find your dream property on St. John?
            </h2>
            <p className="text-white/70 text-sm sm:text-base leading-relaxed">
              Connect with Tammy Donnelly today for personalized guidance on island homes, land, and villa investments.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto shrink-0 z-10">
            <Link
              to="/contact"
              className="px-8 py-4 bg-tropical-500 hover:bg-tropical-600 text-white font-bold rounded-full transition-transform hover:scale-105 duration-300 flex items-center justify-center gap-2 text-sm shadow-lg"
            >
              Contact Us <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:+13406436068"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full transition-colors flex items-center justify-center gap-2 text-sm border border-white/20"
            >
              <PhoneCall className="w-4 h-4 text-tropical-400" /> +1 340-643-6068
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
